/* import __COLOCATED_TEMPLATE__ from './cascade-drawer.hbs'; */
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import config from 'garaje/config/environment';
import type {
  DisplayAuditEntry,
  CascadeStepItem,
  CascadeStepLeaf,
  CascadeStepGroupItem,
  CascadeSubstep,
  AttemptEntry,
} from 'garaje/pods/location-overview/hris-dashboard/audit-log/controller';
import type StateService from 'garaje/services/state';

interface CascadeDrawerArgs {
  entry: DisplayAuditEntry | null;
  cascadeStepsUpToCurrent: Array<{ id: number; step_type?: string; result?: string; activity: string }>;
  hasPrev: boolean;
  hasNext: boolean;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onSelectStep: (id: number) => void;
  onCopyField: (value: string) => void;
}

interface RoutineContextResponse {
  selected_event_uuid: string;
  routine: RoutineMetaApi | null;
  step_groups: StepGroupApiView[];
  routine_events: RoutineAuditEntry[];
}

interface RoutineMetaApi {
  correlation_id: string | null;
  lifecycle_event_type: string | null;
  employee_hris_id: string | null;
  grace_period_seconds: number | null;
  initiated_by_label: string | null;
  state: string | null;
  created_at: string | null;
}

interface StepGroupApiView {
  step_type: string;
  step_display_name: string | null;
  scope: string;
  aggregate_status: string | null;
  is_selected: boolean;
  leaves: StepLeafApiView[];
}

interface StepLeafApiView {
  is_selected: boolean;
  integration_type: string | null;
  location_id: number | null;
  location_name: string | null;
  mode: string | null;
  latest_result: string | null;
  attempts: RoutineAuditEntry[];
}

interface RoutineAuditEntry {
  id: number;
  audit_event_uuid: string;
  audit_event_kind: string;
  step_display_name?: string | null;
  display_status?: string | null;
  result?: string | null;
  attempt_number?: number | null;
  created_at: string;
}

function formatTime(iso: string, tz: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const timeStr = d.toLocaleTimeString('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  const abbr =
    new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(d)
      .find((p) => p.type === 'timeZoneName')?.value ?? '';
  return abbr ? `${timeStr} ${abbr}` : timeStr;
}

const POST_STEP_KINDS = new Set([
  'ROUTINE_COMPLETED',
  'ROUTINE_FAILED',
  'ROUTINE_REVERSE_COMPLETED',
  'ROUTINE_STUCK_RECOVERED',
  'NOTIFICATION_SENT',
]);

const INTEGRATION_LABELS: Record<string, string> = {
  BRIVO: 'Brivo',
  CCURE: 'C-Cure',
  GENEA: 'Genea',
  GENETEC: 'Genetec',
  KANTECH: 'Kantech',
  NETBOX: 'NetBox',
  S2: 'S2',
};

function integrationLabel(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  if (INTEGRATION_LABELS[raw]) return INTEGRATION_LABELS[raw];
  return raw.charAt(0) + raw.slice(1).toLowerCase();
}

function formatGracePeriod(seconds: number | null | undefined): string | undefined {
  if (seconds == null) return undefined;
  if (seconds === 0) return '0 minutes';
  if (seconds % 86400 === 0 && seconds >= 86400) {
    const d = seconds / 86400;
    return `${d} ${d === 1 ? 'day' : 'days'}`;
  }
  if (seconds % 3600 === 0 && seconds >= 3600) {
    const h = seconds / 3600;
    return `${h} ${h === 1 ? 'hour' : 'hours'}`;
  }
  const m = Math.floor(seconds / 60);
  return `${m} ${m === 1 ? 'minute' : 'minutes'}`;
}

export default class CascadeDrawer extends Component<{ Args: CascadeDrawerArgs }> {
  @service declare state: StateService;

  @tracked routineContext: RoutineContextResponse | null = null;
  private fetchAbortController: AbortController | null = null;

  get employeeHrisId(): string | null | undefined {
    return this.routineContext?.routine?.employee_hris_id;
  }

  get gracePeriod(): string | undefined {
    return formatGracePeriod(this.routineContext?.routine?.grace_period_seconds);
  }

  get cascadeStepGroups(): CascadeStepItem[] {
    const entry = this.args.entry;
    if (!entry) return [];
    const ctx = this.routineContext;

    if (ctx) {
      const selectedUuid = entry.audit_event_uuid;
      const result: CascadeStepItem[] = [];

      for (const ev of ctx.routine_events) {
        if (POST_STEP_KINDS.has(ev.audit_event_kind)) continue;
        const leaf: CascadeStepLeaf = {
          type: 'leaf',
          id: ev.id,
          label: ev.step_display_name ?? ev.audit_event_kind,
          status: ev.display_status ?? ev.result ?? ev.audit_event_kind,
          timestamp: '',
          isActive: ev.audit_event_uuid === selectedUuid,
        };
        result.push(leaf);
      }

      const tz = this.state.currentLocation?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      for (const group of ctx.step_groups) {
        const substeps: CascadeSubstep[] = group.leaves.map((leaf) => {
          const completedAttempts: AttemptEntry[] = (leaf.attempts ?? [])
            .filter((a) => a.result != null)
            .map((a) => ({
              attemptNumber: a.attempt_number ?? null,
              status: a.display_status ?? a.result ?? '',
              timestamp: formatTime(a.created_at, tz),
            }));
          const firstAttemptId = (leaf.attempts ?? []).find((a) => a.result != null)?.id;
          return {
            label:
              [integrationLabel(leaf.integration_type), leaf.location_name].filter(Boolean).join(' @ ') || 'Global',
            status: leaf.latest_result ?? 'UNKNOWN',
            timestamp: completedAttempts[0]?.timestamp,
            isActive: leaf.is_selected,
            entryId: firstAttemptId,
            attempts: completedAttempts.length > 1 ? completedAttempts : undefined,
          };
        });
        const succeeded = substeps.filter((s) => s.status === 'SUCCESS').length;
        const failed = substeps.filter((s) => s.status === 'FAILURE' || s.status === 'PARTIAL_FAILURE').length;
        const skipped = substeps.filter((s) => s.status.startsWith('SKIPPED')).length;
        const parts: string[] = [];
        if (succeeded) parts.push(`${succeeded} succeeded`);
        if (failed) parts.push(`${failed} failed`);
        if (skipped) parts.push(`${skipped} skipped`);
        const groupItem: CascadeStepGroupItem = {
          type: 'group',
          id: group.step_type,
          label: group.step_display_name ?? group.step_type,
          status: group.aggregate_status ?? 'SUCCESS',
          summary: parts.join(' · '),
          isActive: group.is_selected,
          defaultOpen: group.is_selected,
          substeps,
        };
        result.push(groupItem);
      }

      for (const ev of ctx.routine_events) {
        if (!POST_STEP_KINDS.has(ev.audit_event_kind)) continue;
        const leaf: CascadeStepLeaf = {
          type: 'leaf',
          id: ev.id,
          label: ev.step_display_name ?? ev.audit_event_kind,
          status: ev.display_status ?? ev.result ?? ev.audit_event_kind,
          timestamp: '',
          isActive: ev.audit_event_uuid === selectedUuid,
        };
        result.push(leaf);
      }

      return result;
    }

    return [];
  }

  @action
  onEntryInserted(): void {
    if (this.args.entry) void this.fetchRoutineContext(this.args.entry);
  }

  @action
  onEntryChanged(_element: Element, [entry]: [DisplayAuditEntry | null]): void {
    this.routineContext = null;
    if (entry) void this.fetchRoutineContext(entry);
  }

  private async fetchRoutineContext(entry: DisplayAuditEntry): Promise<void> {
    if (!entry.audit_event_uuid) return;
    this.fetchAbortController?.abort();
    const controller = new AbortController();
    this.fetchAbortController = controller;
    const host = config.hrisLifecycleApiHost || '';
    const url = `${host}/a/hris-lifecycle/api/v1/audit-events/${encodeURIComponent(entry.audit_event_uuid)}/routine`;
    try {
      const res = await fetch(url, { signal: controller.signal, credentials: 'include' });
      if (!res.ok) return;
      this.routineContext = (await res.json()) as RoutineContextResponse;
    } catch (e) {
      if ((e as Error).name !== 'AbortError') throw e;
    }
  }
}
